// components/task-detail/TaskDetail.jsx
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  AnimatePresence,
  motion,
  useAnimation,
  useReducedMotion,
} from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RRule } from "rrule";
import {
  BsPlusLg,
  BsArrowRepeat,
  BsEmojiSmileUpsideDown,
  BsTrash,
} from "react-icons/bs";
import { FiSquare, FiCheckSquare } from "react-icons/fi";

import Avatar from "../retro/avatar/avatar.component";
import Message from "../message/message.component";
import Subtasks from "../dnd/subtasks/subtasks.component";
import Priority from "../modules/priority/priority.component";
import Assign from "../modules/assign/assign-component";
import ItemAddress from "../item-address/ItemAddress";
import CreatedBy from "../modules/created-by/created-by.component";
import CreatedDate from "../modules/created-date/created-date.component";
import DaysLeft from "../modules/days-left/days-left.component";
import ReactionPickerButton from "../reactions/ReactionPickerButton";
import ReactionSummary from "../reactions/ReactionSummary";
import Duration from "../duration/duration.component";
import Recurring from "../recurring/recurring.component";
import ProgressBar from "../progress-bar/progress-bar.component";
import EmojiPicker from "../emoji/emojiPicker";
import AntEditor from "../editor/anteditor/anteditor";
import useImageUpload from "../../hooks/useImageUpload";
import UploadOverlay from "../upload/UploadOverlay";
import InvitetoPremium from "../marketing/invite-to-premium.component";
import { DEFAULT_PRIORITY_VALUE } from "../../utils/priorities";
import {
  db,
  createNewTask,
  createNewInboxTaskPlus,
  updateTaskName,
  updateTaskDescription,
  updateTaskDone,
  updateTaskAllDay,
  updateTaskDuration,
  updateTaskRrule,
  resetTaskRrule,
  updateTaskMilestone,
  updateTaskStart,
  updateTaskEnd,
  updateTaskProgress,
  createCommentToTask,
  deleteTask,
} from "../../firebase/firebase.utils";
import "./TaskDetail.scss";

const EMPTY = [];
const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
const projectIdOf = (project) => project?.stationId || project?.id || "";

// All of these helpers must return their Firestore update promise.
const TASK_UPDATES = {
  title: updateTaskName,
  description: updateTaskDescription,
  duration: updateTaskDuration,
  done: updateTaskDone,
  allDay: updateTaskAllDay,
  startdate: updateTaskStart,
  enddate: updateTaskEnd,
  type: updateTaskMilestone,
  progress: updateTaskProgress,
  rrule: updateTaskRrule,
};
const sameValue = (a, b) =>
  (a instanceof Date ? a.getTime() : a) ===
  (b instanceof Date ? b.getTime() : b);

// Display older plain-text descriptions safely without changing stored data on open.
function descriptionToHTML(value = "") {
  if (
    /<\/?(?:p|div|br|span|img|h[1-6]|ul|ol|li|b|strong|i|em|u|s|del|blockquote|pre|code|a|details|summary|table|input)(?:\s|\/?>)/i.test(
      value,
    )
  )
    return value;
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\r?\n/g, "<br>");
}

function toDate(value) {
  if (!value) return null;
  const date = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function readRule(value) {
  try {
    if (value) return RRule.fromString(value);
  } catch (err) {
    console.error("Invalid saved recurrence:", err);
  }
  return new RRule({ freq: RRule.DAILY, interval: 1 });
}

function initialForm(source = {}) {
  const startdate = toDate(source.startdate ?? source.startDate);
  const enddate = toDate(source.enddate ?? source.endDate);
  let duration = source.duration ?? "00:30";
  if (source.duration == null && startdate && enddate && enddate >= startdate) {
    const minutes = Math.round((enddate - startdate) / 60000);
    duration = `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
  }
  return {
    title: source.title || "",
    description: source.description || "",
    startdate,
    enddate,
    duration,
    allDay:
      source.allDay ??
      (startdate && enddate
        ? startdate.getHours() === enddate.getHours() &&
          startdate.getMinutes() === enddate.getMinutes()
        : false),
    done: source.done ?? false,
    progress: source.progress ?? 0,
    type: source.type || "task",
    priority: source.priority ?? DEFAULT_PRIORITY_VALUE,
    rrule: source.rrule ? source.rrule.toString() : null,
  };
}

// Resolve the existing task before mounting the form. The key gives each
// task/add route its own draft, without resetting it on every Redux update.
export default function TaskDetail({
  mode = "edit",
  task,
  event,
  station,
  stationTasks = EMPTY,
}) {
  const currentUser = useSelector((state) => state.user.currentUser);
  const assignedTasks = useSelector((state) => state.tasks.tasks) || EMPTY;
  const { wspaceid, projectid, taskId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdd = mode === "add";
  const storedTask = isAdd
    ? null
    : stationTasks.find((item) => item.id === taskId) ||
      assignedTasks.find((item) => item.id === taskId) ||
      (task?.id && (!taskId || task.id === taskId) ? task : null) ||
      (event?.id && (!taskId || event.id === taskId) ? event : null);
  const [liveTask, setLiveTask] = useState(null);
  const sourceSpace = storedTask?.fromSpaceId || wspaceid;
  const sourceStation = storedTask?.fromStationId || projectid;
  const sourceId = taskId || storedTask?.id;
  const taskPath = !isAdd && currentUser?.uid && sourceId && sourceSpace
    ? sourceSpace === `${currentUser.uid}-Inbox`
      ? `users/${currentUser.uid}/tasks/${sourceId}`
      : sourceStation ? `space/${sourceSpace}/stations/${sourceStation}/tasks/${sourceId}` : null
    : null;
  useEffect(() => {
    if (!taskPath) return;
    return db.doc(taskPath).onSnapshot(snapshot => setLiveTask({path:taskPath, data:snapshot.exists ? {...snapshot.data(), id:snapshot.id} : null}), error=>console.log("Task moderation update:", error));
  }, [taskPath]);
  const currentTask = liveTask?.path === taskPath ? liveTask.data : storedTask;
  const habitsState = useSelector((state) => state.habits);

  const habits = Array.isArray(habitsState)
    ? habitsState
    : habitsState?.habits || EMPTY;

  const relatedHabit =
    currentTask?.type === "habitReminder" && currentTask?.habitId
      ? habits.find((habit) => habit.id === currentTask.habitId)
      : null;

  if (!currentUser) return null;
  if (currentTask?.moderationHidden === true) return createPortal(
    <div className="TaskDetail"><div className="td-overlay" onClick={()=>navigate(-1)} /><div className="td-panel" role="dialog" aria-modal="true" aria-label="Removed task"><p role="status">This task was removed by moderation.</p><button type="button" className="td-button" onClick={()=>navigate(-1)}>Close</button></div></div>, document.body);

  if (!isAdd && !currentTask) {
    return createPortal(
      <div className="TaskDetail">
        <div className="td-overlay" onClick={() => navigate(-1)} />
        <div
          className="td-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Task"
        >
          <p role="status">
            Waiting for task data. If it does not appear, it may be unavailable.
          </p>
          <button
            type="button"
            className="td-button"
            onClick={() => navigate(-1)}
          >
            Close
          </button>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <TaskDetailForm
      key={JSON.stringify([
        currentUser.uid,
        mode,
        wspaceid,
        projectid,
        taskId || currentTask?.id,
        location.key,
      ])}
      isAdd={isAdd}
      currentTask={currentTask}
      currentUser={currentUser}
      relatedHabit={relatedHabit}
      station={station}
      wspaceid={wspaceid}
      projectid={projectid}
      initialData={{ ...event, ...location.state }}
    />,
    document.body,
  );
}

function TaskDetailForm({
  isAdd,
  currentTask,
  currentUser,
  relatedHabit,
  station,
  wspaceid,
  projectid,
  initialData,
}) {
  const navigate = useNavigate();
  const stations = useSelector((state) => state.space.stationData) || EMPTY;
  const inboxId = `${currentUser.uid}-Inbox`;
  const [form, setForm] = useState(() =>
    initialForm(isAdd ? initialData : currentTask),
  );
  const [address, setAddress] = useState(() => ({
    spaceId: isAdd
      ? station?.fromSpaceId || station?.spaceId || wspaceid || inboxId
      : currentTask.fromSpaceId,
    stationId: isAdd ? projectid || "" : currentTask.fromStationId || "",
    phaseId: isAdd ? "" : currentTask.phase || "",
  }));
  const { spaceId, stationId, phaseId } = address;
  const isQuick = spaceId === inboxId;
  const isHabitReminder = !isAdd && currentTask?.type === "habitReminder";

  const isPersonalTask = isQuick || isHabitReminder;
  const taskId = currentTask?.id;
  const [docImgFile, setDocImgFile] = useState(null);
  const [docImgFileAltText, setDocImgFileAltText] = useState("");
  const { uploading, progress, status, uploadImage } = useImageUpload({
    userId: currentUser.uid,
    getBasePath: () =>
      isQuick
        ? `users/${currentUser.uid}/taskImages`
        : spaceId && stationId
          ? `space/${spaceId}/stations/${stationId}/taskImages`
          : "",
  });
  const handleUploadDocImage = async () => {
    const { url } = await uploadImage(docImgFile);
    return url;
  };
  const [phases, setPhases] = useState(EMPTY);
  const [phasesLoading, setPhasesLoading] = useState(false);
  const [comments, setComments] = useState(EMPTY);
  const [comment, setComment] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [more, setMore] = useState(!isAdd && !isQuick);
  const [repeat, setRepeat] = useState(false);
  const [showInvitation, setShowInvitation] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  const [error, setError] = useState("");
  const commentInput = useRef(null);
  const doneAudio = useRef(null);
  const lastSaved = useRef({ ...form });
  const exitState = useRef({ form, skipSave: false });
  const panelRef = useRef(null);
  const opening = useAnimation();
  const reducedMotion = useReducedMotion();
  const [openingOrigin] = useState(initialData.taskOrigin || null);

  useLayoutEffect(() => {
    exitState.current.form = form;
  }, [form]);

  // Animation controls mount in an effect in some Framer Motion versions.
  // Keep this after mount; the panel starts hidden until it is positioned.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const origin = openingOrigin;
    const hasOrigin =
      origin &&
      [origin.x, origin.y, origin.width, origin.height].every(
        Number.isFinite,
      ) &&
      origin.width > 0 &&
      origin.height > 0 &&
      rect.width > 0 &&
      rect.height > 0;
    opening.set(
      reducedMotion
        ? { opacity: 1, x: 0, y: 0, scaleX: 1, scaleY: 1 }
        : {
            opacity: 0,
            x: hasOrigin ? origin.x - rect.left : 0,
            y: hasOrigin ? origin.y - rect.top : 0,
            scaleX: hasOrigin ? origin.width / rect.width : 1,
            scaleY: hasOrigin ? origin.height / rect.height : 1,
          },
    );
    opening.start({
      opacity: 1,
      x: 0,
      y: 0,
      scaleX: 1,
      scaleY: 1,
      transition: {
        duration: reducedMotion ? 0 : hasOrigin ? 0.28 : 0.16,
        ease: [0.22, 1, 0.36, 1],
      },
    });
    return () => opening.stop();
  }, [opening, openingOrigin, reducedMotion]);
  const previousTask = useRef(currentTask);
  // Serialize writes so closing immediately after an autosave cannot let an
  // older title/description finish after the newest value.
  const saveQueue = useRef(Promise.resolve());

  // Keep unsaved input even if Firestore briefly echoes a pending write and
  // later rolls it back. Only untouched/successfully saved fields take remote updates.
  useEffect(() => {
    if (isAdd || currentTask === previousTask.current) return;
    const after = initialForm(currentTask);
    previousTask.current = currentTask;
    const next = { ...form };
    for (const field of Object.keys(after)) {
      if (sameValue(form[field], lastSaved.current[field])) {
        next[field] = after[field];
        lastSaved.current[field] = after[field];
      }
    }
    setForm(next);
  }, [currentTask, isAdd, form]);

  const selectedProject =
    stations.find(
      (item) =>
        projectIdOf(item) === stationId &&
        (item.fromSpaceId || item.spaceId) === spaceId,
    ) || (projectIdOf(station) === stationId ? station : null);
  const [initialRRule, setInitialRRule] = useState(() => readRule(form.rrule));
  const [modifiedRRule, setModifiedRRule] = useState(null);

  const reportError = useCallback((err) => {
    console.error("Task operation failed:", err);
    setError("Could not save this change. Please try again.");
  }, []);

  // ItemAddress owns the picker options; this parent loads phases and saves tasks.
  const phaseOrder = JSON.stringify(selectedProject?.phaseOrder || []);
  useEffect(() => {
    setPhases(EMPTY);
    setPhasesLoading(false);
    if (!spaceId || !stationId || isPersonalTask) return;
    setPhasesLoading(true);
    const order = JSON.parse(phaseOrder);
    return db
      .collection("space")
      .doc(spaceId)
      .collection("stations")
      .doc(stationId)
      .collection("phases")
      .onSnapshot(
        (snapshot) => {
          const list = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          list.sort((a, b) => {
            const aIndex = order.indexOf(a.id),
              bIndex = order.indexOf(b.id);
            return (
              (aIndex < 0 ? Infinity : aIndex) -
                (bIndex < 0 ? Infinity : bIndex) || byName(a, b)
            );
          });
          setPhases(list);
          setPhasesLoading(false);
          if (isAdd)
            setAddress((previous) => {
              if (
                previous.spaceId !== spaceId ||
                previous.stationId !== stationId
              )
                return previous;
              const nextPhase = list.some(
                (item) => item.id === previous.phaseId,
              )
                ? previous.phaseId
                : list[0]?.id || "";
              return nextPhase === previous.phaseId
                ? previous
                : { ...previous, phaseId: nextPhase };
            });
        },
        (err) => {
          setPhasesLoading(false);
          reportError(err);
        },
      );
  }, [spaceId, stationId, isQuick, isAdd, phaseOrder, reportError]);

  useEffect(() => {
    if (isAdd || isPersonalTask || !spaceId || !stationId || !taskId) return;
    return db
      .collection("space")
      .doc(spaceId)
      .collection("stations")
      .doc(stationId)
      .collection("tasks")
      .doc(taskId)
      .collection("comments")
      .orderBy("created", "desc")
      .limit(15)
      .onSnapshot((snapshot) => {
        setComments(
          snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
        );
      }, reportError);
  }, [isAdd, isQuick, spaceId, stationId, taskId, reportError]);

  // Each queued save compares against the last successful write. A failed field
  // stays dirty and is retried on close, together with the latest text edits.
  const queueSave = useCallback(
    (values) => {
      if (isAdd || isHabitReminder) return Promise.resolve();
      const save = async () => {
        for (const field of Object.keys(TASK_UPDATES)) {
          if (
            !(field in values) ||
            sameValue(values[field], lastSaved.current[field])
          )
            continue;
          if (field === "rrule" && !values[field]) {
            await resetTaskRrule(spaceId, stationId, currentUser.uid, taskId);
          } else {
            await TASK_UPDATES[field](
              spaceId,
              stationId,
              currentUser.uid,
              taskId,
              values[field],
            );
          }
          lastSaved.current[field] = values[field];
        }
      };
      const next = saveQueue.current.catch(() => {}).then(save);
      saveQueue.current = next;
      return next;
    },
    [isAdd, isHabitReminder, spaceId, stationId, currentUser.uid, taskId],
  );

  useEffect(() => {
    if (isAdd || busy) return;
    const timer = setTimeout(() => {
      queueSave(form).catch(reportError);
    }, 3000);
    return () => clearTimeout(timer);
  }, [isAdd, busy, form, queueSave, reportError]);

  useEffect(() => {
    if (isAdd) return;
    // Browser Back / other in-app navigation bypasses the modal's Close button.
    // Start the pending save on unmount; the explicit Close button waits for it.
    const warnBeforeLeaving = (event) => {
      if (exitState.current.skipSave) return;
      const draft = exitState.current.form;
      if (
        !Object.keys(TASK_UPDATES).some(
          (field) => !sameValue(draft[field], lastSaved.current[field]),
        )
      )
        return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => {
      window.removeEventListener("beforeunload", warnBeforeLeaving);
      if (!exitState.current.skipSave) {
        queueSave(exitState.current.form).catch((err) =>
          console.error("Could not save task after navigation:", err),
        );
      }
    };
  }, [isAdd, queueSave]);

  function changeField(field, value, saveFunction) {
    if (busy || isHabitReminder) return;

    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setError("");

    if (!isAdd && saveFunction) {
      queueSave({ [field]: value }).catch(reportError);
    }
  }

  const onRRuleChange = useCallback((rule) => {
    const text = rule ? rule.toString() : null;
    setForm((previous) =>
      previous.rrule === text ? previous : { ...previous, rrule: text },
    );
  }, []);
  // Support the callback used by edit mode and the setter used by the former add form.
  useEffect(() => {
    if (modifiedRRule !== null) onRRuleChange(modifiedRRule);
  }, [modifiedRRule, onRRuleChange]);

  async function closeTask() {
    if (busy || uploading) return;
    setBusy(true);
    try {
      await queueSave(exitState.current.form);
      exitState.current.skipSave = true;
      navigate(-1);
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  }

  async function saveTask(e) {
    e.preventDefault();
    if (busy || uploading) return;
    if (!form.title.trim()) {
      setError("Please give the task a title first.");
      return;
    }
    if (
      isAdd &&
      !isQuick &&
      (!spaceId || !stationId || !phaseId || phasesLoading)
    ) {
      setError("Please select a project and phase first.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (isAdd) {
        if (isQuick)
          await createNewInboxTaskPlus(
            currentUser.uid,
            form.title.trim(),
            form.description,
            form.startdate,
            form.enddate,
            form.type,
            form.allDay,
            form.duration,
            form.progress,
            form.rrule,
            form.priority,
          );
        else
          await createNewTask(
            spaceId,
            stationId,
            phaseId,
            form.title.trim(),
            form.description,
            currentUser.uid,
            form.startdate,
            form.enddate,
            form.type,
            form.allDay,
            form.duration,
            form.progress,
            form.rrule,
            form.priority,
          );
      } else await queueSave(exitState.current.form);
      exitState.current.skipSave = true;
      navigate(-1);
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  }

  async function removeTask() {
    if (busy || uploading || isAdd) return;
    setBusy(true);
    exitState.current.skipSave = true;
    try {
      await saveQueue.current.catch(() => {});
      await deleteTask(
        spaceId,
        stationId,
        currentTask.phase,
        currentUser.uid,
        currentTask,
      );
      navigate(-1);
    } catch (err) {
      exitState.current.skipSave = false;
      reportError(err);
    } finally {
      setBusy(false);
    }
  }

  function commentRef(id) {
    return db
      .collection("space")
      .doc(spaceId)
      .collection("stations")
      .doc(stationId)
      .collection("tasks")
      .doc(taskId)
      .collection("comments")
      .doc(id);
  }

  async function sendComment() {
    if (!comment.trim() || sendingComment) return;
    const text = comment;
    setSendingComment(true);
    try {
      await createCommentToTask(
        spaceId,
        stationId,
        text,
        currentUser.uid,
        taskId,
        currentUser.userName,
      );
      setComment((previous) => (previous === text ? "" : previous));
    } catch (err) {
      reportError(err);
    } finally {
      setSendingComment(false);
    }
  }

  return (
    <div className="TaskDetail">
      <UploadOverlay visible={uploading} progress={progress} status={status} />
      <div className="td-overlay" onClick={closeTask} />
      <motion.div
        ref={panelRef}
        className="td-panel"
        role="dialog"
        aria-modal="true"
        aria-label={isAdd ? "Add task" : "Task details"}
        initial={{ opacity: 0 }}
        animate={opening}
        style={{ transformOrigin: "top left" }}
      >
        <div className="td-top-row">
          <ItemAddress
            type="task"
            task={currentTask}
            relatedHabit={relatedHabit}
            spaceId={spaceId}
            stationId={stationId}
            phaseId={phaseId}
            phases={phases}
            phasesLoading={phasesLoading}
            setSpaceId={(value) =>
              setAddress((previous) => ({
                ...previous,
                spaceId: value,
              }))
            }
            setStationId={(value) =>
              setAddress((previous) => ({
                ...previous,
                stationId: value,
              }))
            }
            setPhaseId={(value) =>
              setAddress((previous) => ({
                ...previous,
                phaseId: value,
              }))
            }
            readOnly={!isAdd}
            disabled={busy || uploading}
            compactWorkspace
            style={{
              padding: 0,
              flex: 1,
              marginLeft: -6,
            }}
          />
          <button
            type="button"
            className="td-close"
            aria-label="Close task"
            onClick={closeTask}
            disabled={busy || uploading}
          >
            <BsPlusLg />
          </button>
        </div>

        {error && (
          <p className="td-error" role="alert">
            {error}
          </p>
        )}
        <div>
          <div className="td-title-row">
            {!isAdd && (
              <button
                type="button"
                className={`td-done ${form.done ? "is-done" : ""}`}
                aria-label={form.done ? "Mark incomplete" : "Mark complete"}
                disabled={busy}
                onClick={() => {
                  changeField("done", !form.done, updateTaskDone);
                  doneAudio.current?.play()?.catch(() => {});
                }}
              >
                {form.done ? <FiCheckSquare /> : <FiSquare />}
              </button>
            )}
            {/* The hidden text copy lets the title grow and wrap without a scrollbar. */}
            <div className="td-title-field" data-title={form.title + "\n"}>
              <textarea
                aria-label="Task title"
                autoFocus={isAdd}
                className="td-title"
                rows={2}
                placeholder="Task / Event Name..."
                value={form.title}
                disabled={busy || isHabitReminder}
                onChange={(e) => changeField("title", e.target.value)}
              />
            </div>
            {!isAdd && <Assign task={currentTask} />}
          </div>
          <div className="td-description-row">
            <div
              style={{ flex: 1, minWidth: 0, maxWidth: "100%" }}
              role="group"
              aria-label="Description"
            >
              <AntEditor
                mainToolbarOn={true}
                floatingToolbarOn={true}
                placeholder="Add description"
                initialValue={descriptionToHTML(form.description)}
                setDocContent={(html) => changeField("description", html)}
                setDocImgFile={setDocImgFile}
                setDocImgFileAltText={setDocImgFileAltText}
                docImgFileAltText={docImgFileAltText}
                handleUploadDocImage={handleUploadDocImage}
                editability={!busy && !isHabitReminder}
              />
            </div>
          </div>
        </div>
        <div className="td-module-row">
          <label className="td-checkbox">
            <input
              type="checkbox"
              checked={form.allDay}
              disabled={busy}
              onChange={(e) =>
                changeField("allDay", e.target.checked, updateTaskAllDay)
              }
            />
            All day
          </label>
          <Duration
            event={currentTask || form}
            open={false}
            duration={form.duration}
            setDuration={(value) => changeField("duration", value)}
            disabled={busy}
            setShowInvitation={setShowInvitation}
            showInvitation={showInvitation}
            currentUser={currentUser}
            handleUpdateTaskDuration={(value) =>
              changeField("duration", value, updateTaskDuration)
            }
          />
          {isAdd ? (
              <Priority
                value={form.priority}
                onChange={(value) => changeField("priority", value)}
                spaceId={spaceId}
                stationId={stationId}
                station={selectedProject}
              />
            ) : (
              <Priority task={currentTask} station={selectedProject} />
            )}
                      <button
            type="button"
            className="td-repeat"
            title="Repeat"
            aria-expanded={repeat}
            onClick={() => {
              if (!repeat) {
                setInitialRRule(readRule(form.rrule));
                setModifiedRRule(null);
              }
              setRepeat(!repeat);
            }}
          >
            <BsArrowRepeat />
            {form.rrule ? "Recurring" : "Repeat"}
          </button>
        </div>
        <div className="td-dates">
          <div>
            <label>start</label>
            <DatePicker
              selected={form.startdate}
              isClearable
              disabled={busy}
              placeholderText={
                form.allDay ? "Select a start date" : "Select a start time"
              }
              showTimeSelect={!form.allDay}
              timeFormat="p"
              timeIntervals={15}
              dateFormat={form.allDay ? "MMMM d, yyyy" : "MMM d, yy | hh:mm aa"}
              todayButton="today"
              wrapperClassName="td-datepicker"
              calendarClassName={!form.allDay ? "td-calendar-with-time" : ""}
              popperClassName="td-calendar"
              popperPlacement="bottom-start"
              popperProps={{ strategy: "fixed" }}
              onChange={(date) =>
                changeField("startdate", date, updateTaskStart)
              }
            />
          </div>
          <div>
            <label>end</label>
            <DatePicker
              selected={form.enddate}
              isClearable
              disabled={busy}
              placeholderText="Select end or deadline"
              showTimeSelect={!form.allDay}
              timeFormat="p"
              timeIntervals={15}
              dateFormat={form.allDay ? "MMMM d, yyyy" : "MMM d, yy | hh:mm aa"}
              todayButton="today"
              wrapperClassName="td-datepicker"
              calendarClassName={!form.allDay ? "td-calendar-with-time" : ""}
              popperClassName="td-calendar"
              popperPlacement="bottom-end"
              popperProps={{ strategy: "fixed" }}
              onChange={(date) => changeField("enddate", date, updateTaskEnd)}
            />
          </div>
        </div>
        <div className="td-module-row">
          {!isAdd && !repeat && (
            <Subtasks task={currentTask} open opacity="opacityTrue" />
          )}
        </div>
        {repeat && (
          <div className="td-recurring">
            <Recurring
              initialRRule={initialRRule}
              onRRuleChange={onRRuleChange}
              modifiedRRule={modifiedRRule}
              setModifiedRRule={setModifiedRRule}
              rruleString={form.rrule}
              startDateRef={form.startdate}
            />
            {form.rrule && (
              <button
                type="button"
                className="td-button"
                onClick={() => {
                  setModifiedRRule(null);
                  changeField("rrule", null);
                  setRepeat(false);
                }}
              >
                Cancel Repeat
              </button>
            )}
          </div>
        )}
        {isAdd ? (
          <form className="td-footer" onSubmit={saveTask}>
            {/* Child modules stay outside this form so their buttons cannot submit it. */}
            <button
              className="td-button td-add"
              type="submit"
              disabled={busy || uploading || !form.title.trim()}
            >
              {busy ? "Saving…" : "Add Task"}
            </button>
          </form>
        ) : (
          <div className="td-footer">
            <button
              type="button"
              className="td-button td-delete"
              title="Delete task"
              aria-label="Delete task"
              disabled={busy || uploading}
              onClick={removeTask}
            >
              <BsTrash />
            </button>
            {!isQuick && (
              <button
                type="button"
                className="td-more"
                aria-expanded={more}
                onClick={() => setMore(!more)}
              >
                {more ? "less..." : "more..."}
              </button>
            )}
          </div>
        )}

        <AnimatePresence initial={false}>
          {!isAdd && !isQuick && more && (
            <motion.div
              key="task-extra"
              className="td-extra-wrap"
              initial={{ height: 0, opacity: 0, overflow: "hidden" }}
              animate={{
                height: "auto",
                opacity: 1,
                transitionEnd: { overflow: "visible" },
              }}
              exit={{ height: 0, opacity: 0, overflow: "hidden" }}
              transition={{
                duration: reducedMotion ? 0 : 0.22,
                ease: "easeInOut",
              }}
            >
              <div className="td-extra">
                <div className="td-progress">
                  <ProgressBar
                    progress={form.progress}
                    completed={form.progress}
                    bgcolor="#3b3b3b"
                    setProgress={(value) =>
                      setForm((previous) => ({ ...previous, progress: value }))
                    }
                    handleUpdateTaskProgress={(value) =>
                      changeField("progress", value, updateTaskProgress)
                    }
                  />
                </div>
                <label className="td-checkbox">
                  <input
                    type="checkbox"
                    checked={form.type === "milestone"}
                    disabled={busy}
                    onChange={(e) =>
                      changeField(
                        "type",
                        e.target.checked ? "milestone" : "task",
                        updateTaskMilestone,
                      )
                    }
                  />
                  Milestone
                </label>
                <div className="td-metadata">
                  <div>
                    <label>Created by</label>
                    <CreatedBy task={currentTask} />
                  </div>
                  <div>
                    <label>Created at</label>
                    <CreatedDate task={currentTask} />
                  </div>
                  <div>
                    <label>Day</label>
                    <DaysLeft task={currentTask} />
                  </div>
                </div>
                <div className="td-comments">
                  {comments.map((msg) => msg.moderationHidden ? <div className="td-comment" key={msg.id}><p role="status" style={{fontStyle:"italic"}}>This comment was removed by moderation.</p></div> : (
                    <div
                      key={msg.id}
                      className={`td-comment ${msg.from === currentUser.uid ? "is-self" : ""}`}
                    >
                      <div className="td-message">
                        <Message msg={msg} />
                        <ReactionSummary
                          docRef={commentRef(msg.id)}
                          currentUser={currentUser}
                          isSelf={msg.from === currentUser.uid}
                        />
                      </div>
                      <ReactionPickerButton
                        docRef={commentRef(msg.id)}
                        currentUser={currentUser}
                      />
                      <button
                        type="button"
                        className="td-comment-delete"
                        title="Delete comment"
                        aria-label="Delete comment"
                        onClick={() =>
                          commentRef(msg.id).delete().catch(reportError)
                        }
                      >
                        <BsTrash />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="td-composer">
                  <textarea
                    ref={commentInput}
                    placeholder="Write a comment"
                    aria-label="Write a comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                  <div className="td-composer-actions">
                    <Avatar user={currentUser} src={currentUser.imageUrl} />
                    <div className="td-emoji">
                      <button
                        type="button"
                        aria-label="Choose emoji"
                        onClick={() => setEmojiOpen(!emojiOpen)}
                      >
                        <BsEmojiSmileUpsideDown />
                      </button>
                      {emojiOpen && (
                        <div className="td-emoji-picker">
                          <EmojiPicker
                            onEmojiSelect={(emoji) => {
                              setComment((previous) => previous + emoji.native);
                              setEmojiOpen(false);
                              commentInput.current?.focus();
                            }}
                            onClickOutside={() => setEmojiOpen(false)}
                          />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="td-button td-send"
                      disabled={sendingComment || !comment.trim()}
                      onClick={sendComment}
                    >
                      {sendingComment ? "Sending…" : "Send"}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {showInvitation && (
          <div className="td-premium">
            <InvitetoPremium
              setShowInvitation={setShowInvitation}
              featureName="Duration Estimates"
              featureDesc="set duration estimates, automatically generate start/end time upon dragging a task on a time-block of the calendar"
              featureImg="https://images.unsplash.com/photo-1555255675-1d2d8db2d191?auto=format&fit=crop&w=686&q=80"
              currentUser={currentUser}
            />
          </div>
        )}
        <audio
          ref={doneAudio}
          src="/media/mixkit-cool-interface-click-tone-2568.wav"
        />
      </motion.div>
    </div>
  );
}
